/**
 * Keyboard and on-screen input, sampled once per frame with edge detection.
 *
 * The simulation needs to know both the held state and whether an action went
 * down on this exact frame — that edge is what triggers a jump, so it must be
 * latched here and cleared at the end of every step.
 *
 * Two sources feed the same actions: physical keys, tracked by `code`, and
 * virtual controls (the touch stick and buttons in `core/touch.ts`), tracked by
 * action name. Keeping them separate is what lets the touch UI press "jump"
 * without pretending a keyboard key exists, while both remain edge-latched
 * through the same `beginFrame`/`endFrame` window.
 */

export interface Bindings {
  left: string[]
  right: string[]
  up: string[]
  down: string[]
  jump: string[]
  run: string[]
  /** Editor-only keys. */
  editor: string[]
  confirm: string[]
  cancel: string[]
  restart: string[]
  /** Leave the current course from the pause screen. */
  quit: string[]
}

/** Every action a control can press, keyboard or on-screen. */
export type InputAction = keyof Bindings

export const DEFAULT_BINDINGS: Bindings = {
  left: ['ArrowLeft', 'KeyA'],
  right: ['ArrowRight', 'KeyD'],
  up: ['ArrowUp', 'KeyW'],
  down: ['ArrowDown', 'KeyS'],
  jump: ['Space', 'KeyZ', 'KeyJ'],
  run: ['ShiftLeft', 'ShiftRight', 'KeyX', 'KeyK'],
  editor: ['KeyE'],
  confirm: ['Enter', 'Space'],
  cancel: ['Escape', 'Backspace'],
  restart: ['KeyR'],
  quit: ['KeyQ'],
}

export class Input {
  private held = new Set<string>()
  private pressedThisFrame = new Set<string>()
  private bindings: Bindings
  /** Codes pressed since the last `endFrame()`. */
  private pendingPressed = new Set<string>()
  /** Actions held by the on-screen controls. */
  private virtualHeld = new Set<InputAction>()
  private virtualPressedThisFrame = new Set<InputAction>()
  private pendingVirtual = new Set<InputAction>()

  constructor(bindings: Bindings = DEFAULT_BINDINGS) {
    this.bindings = bindings
  }

  /** Attach DOM listeners. Safe to call once. */
  attach(target: EventTarget = window): () => void {
    const onDown = (e: Event): void => {
      const ke = e as KeyboardEvent
      // Stop the page scrolling out from under the canvas.
      if (
        ke.code === 'Space' ||
        ke.code === 'ArrowUp' ||
        ke.code === 'ArrowDown' ||
        ke.code === 'ArrowLeft' ||
        ke.code === 'ArrowRight'
      ) {
        ke.preventDefault()
      }
      if (ke.repeat) return
      this.held.add(ke.code)
      this.pendingPressed.add(ke.code)
    }
    const onUp = (e: Event): void => {
      const ke = e as KeyboardEvent
      this.held.delete(ke.code)
    }
    /**
     * Drop everything held, without latching releases.
     *
     * A lost focus or a backgrounded tab must not leave a movement key or a
     * touch stick stuck down: there is no keyup coming, so the game would run
     * forever with the last direction. Releases are deliberately not latched —
     * a state the player never chose must not read as an input edge.
     */
    const dropHeld = (): void => {
      this.held.clear()
      this.virtualHeld.clear()
      this.pendingVirtual.clear()
    }
    target.addEventListener('keydown', onDown)
    target.addEventListener('keyup', onUp)
    window.addEventListener('blur', dropHeld)
    document.addEventListener('visibilitychange', dropHeld)
    return () => {
      target.removeEventListener('keydown', onDown)
      target.removeEventListener('keyup', onUp)
      window.removeEventListener('blur', dropHeld)
      document.removeEventListener('visibilitychange', dropHeld)
    }
  }

  /**
   * Press or release an on-screen action. A press latches an edge exactly like
   * a key, so it survives until the next `beginFrame` even when the finger has
   * already lifted — a quick tap must not be lost between frames.
   */
  setVirtual(action: InputAction, down: boolean): void {
    if (down) {
      if (this.virtualHeld.has(action)) return
      this.virtualHeld.add(action)
      this.pendingVirtual.add(action)
      return
    }
    this.virtualHeld.delete(action)
  }

  /** Release every on-screen action without producing press edges. */
  clearVirtual(): void {
    this.virtualHeld.clear()
    this.pendingVirtual.clear()
  }

  /** Latch queued presses. Call once at the top of a frame. */
  beginFrame(): void {
    this.pressedThisFrame = new Set(this.pendingPressed)
    this.pendingPressed.clear()
    this.virtualPressedThisFrame = new Set(this.pendingVirtual)
    this.pendingVirtual.clear()
  }

  /** Clear latches. Call once at the end of a frame. */
  endFrame(): void {
    this.pressedThisFrame.clear()
    this.virtualPressedThisFrame.clear()
  }

  isDown(action: InputAction): boolean {
    return (
      this.virtualHeld.has(action) ||
      this.bindings[action].some((c) => this.held.has(c))
    )
  }

  justPressed(action: InputAction): boolean {
    return (
      this.virtualPressedThisFrame.has(action) ||
      this.bindings[action].some((c) => this.pressedThisFrame.has(c))
    )
  }

  /** Raw code helpers, for menus that need arbitrary keys. */
  codeDown(code: string): boolean {
    return this.held.has(code)
  }
  codePressed(code: string): boolean {
    return this.pressedThisFrame.has(code)
  }
}
